import React from 'react';
import { Box, Typography } from '@mui/material';
import { NavLink, useLocation } from 'react-router-dom';
import {
  Home as HomeIcon,
  GridView as ServicesIcon,
  Bolt as ActivityIcon,
  LocalShipping as ReleasesIcon,
  Public as EnvironmentsIcon,
  Construction as JobsIcon,
  ViewModule as ModulesIcon,
  GridOn as AdoptionIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { getColors, rounded } from '../theme/tokens';
import { duration, reduceMotionSx, transition } from '../theme/motion';

export interface NavItem {
  label: string;
  path: string;
  icon: React.ElementType;
  /** Match nested routes too (e.g. /services/foo highlights Services). */
  matchPrefix?: boolean;
}

export const CD_NAV_ITEMS: NavItem[] = [
  { label: 'Overview', path: '/', icon: HomeIcon },
  { label: 'Services', path: '/services', icon: ServicesIcon, matchPrefix: true },
  { label: 'Timeline', path: '/activity', icon: ActivityIcon },
  { label: 'Releases', path: '/releases', icon: ReleasesIcon },
  { label: 'Environments', path: '/environments', icon: EnvironmentsIcon, matchPrefix: true },
  { label: 'Jobs', path: '/jobs', icon: JobsIcon, matchPrefix: true },
];

export const INFRA_NAV_ITEMS: NavItem[] = [
  { label: 'Overview', path: '/infra', icon: HomeIcon },
  { label: 'Modules', path: '/infra/modules', icon: ModulesIcon, matchPrefix: true },
  { label: 'Adoption', path: '/infra/adoption', icon: AdoptionIcon },
];

export type Workspace = 'cd' | 'infra';

export const workspaceFor = (pathname: string): Workspace =>
  pathname === '/infra' || pathname.startsWith('/infra/') ? 'infra' : 'cd';

export const NAV_ITEMS_BY_WORKSPACE: Record<Workspace, NavItem[]> = {
  cd: CD_NAV_ITEMS,
  infra: INFRA_NAV_ITEMS,
};

// Notion ex-app-shell-row: quiet rows on the soft canvas, 5px radius, the
// single blue accent as the active indicator.
const SidebarNav: React.FC = () => {
  const theme = useTheme();
  const c = getColors(theme.palette.mode);
  const { pathname } = useLocation();

  const isActive = (item: NavItem) =>
    item.matchPrefix ? pathname === item.path || pathname.startsWith(`${item.path}/`) : pathname === item.path;

  return (
    <Box component="nav" sx={{ px: 1, py: 1.5, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
      {NAV_ITEMS_BY_WORKSPACE[workspaceFor(pathname)].map(item => {
        const active = isActive(item);
        const Icon = item.icon;
        return (
          <Box
            key={item.path}
            component={NavLink}
            to={item.path}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.25,
              px: 1.5,
              py: 0.75,
              borderRadius: `${rounded.sm}px`,
              textDecoration: 'none',
              position: 'relative',
              color: active ? c.ink : c.inkMuted,
              backgroundColor: active ? c.hoverWash : 'transparent',
              transition: transition(['color', 'background-color'], duration.fast),
              '&:hover': { backgroundColor: c.hoverWash, color: c.ink },
              '&::before': {
                content: '""',
                position: 'absolute',
                left: 0,
                top: 6,
                bottom: 6,
                width: 3,
                borderRadius: 2,
                backgroundColor: c.primary,
                transformOrigin: 'center',
                transform: active ? 'scaleY(1)' : 'scaleY(0)',
                opacity: active ? 1 : 0,
                transition: transition(['transform', 'opacity'], duration.base),
              },
              ...reduceMotionSx,
            }}
          >
            <Icon sx={{ fontSize: 18, opacity: active ? 1 : 0.75, transition: transition('opacity', duration.fast) }} />
            <Typography
              variant="body2"
              sx={{ fontWeight: active ? 600 : 500, fontSize: '0.9rem', lineHeight: 1.2 }}
            >
              {item.label}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

export default SidebarNav;
